import { useRef, useState } from "react";
import { Camera, Loader2, Globe, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { imageUploadError, IMAGE_ACCEPT, storageRejectionMessage } from "@/lib/storageUploadLimits";
import { ORG_TYPE_OPTIONS } from "@/lib/orgTypeOptions";
import { OrgTypeHelp } from "@/components/organization/OrgTypeHelp";

interface Step2Props {
  data: {
    logo: string;
    tagline: string;
    description: string;
    websiteUrl: string;
    scanWebsite: boolean;
    type: string;
  };
  onUpdate: (data: any) => void;
}

const OrgOnboardingStep2 = ({ data, onUpdate }: Step2Props) => {
  const { user } = useAuth();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Identical upload path to BrandBasicsSection — same bucket, same key shape —
  // so a logo set here is read back unchanged by the profile editor.
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const invalid = imageUploadError(file);
    if (invalid) {
      toast.error(invalid);
      if (logoInputRef.current) logoInputRef.current.value = "";
      return;
    }
    setUploadingLogo(true);
    try {
      const ext = file.name.split(".").pop();
      const filePath = `${user?.id}/logo-${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from("organization-content")
        .upload(filePath, file, { upsert: true });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from("organization-content").getPublicUrl(filePath);
      onUpdate({ ...data, logo: urlData.publicUrl });
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(storageRejectionMessage(err) ?? "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  return (
    <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
      <input ref={logoInputRef} type="file" accept={IMAGE_ACCEPT} className="hidden" onChange={handleLogoUpload} />

      <div className="space-y-5">
        {/* Website — first, so the scan can offer to do the rest */}
        <div className="space-y-2">
          <Label htmlFor="website" className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-primary" />
            Website
          </Label>
          <Input
            id="website"
            type="url"
            value={data.websiteUrl}
            onChange={(e) => onUpdate({ ...data, websiteUrl: e.target.value })}
            placeholder="https://yourwebsite.com"
          />

          {/* Scan toggle — on by default. On Continue, we read the site and fill
              blank fields into the draft; the following steps arrive pre-filled. */}
          <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/60 p-3">
            <Switch
              id="scan-website"
              checked={data.scanWebsite}
              onCheckedChange={(checked) => onUpdate({ ...data, scanWebsite: checked })}
              className="mt-0.5"
            />
            <Label htmlFor="scan-website" className="cursor-pointer space-y-0.5 font-normal">
              <span className="flex items-start gap-1.5 text-sm font-medium text-foreground">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold-ink" />
                Scan my website and fill in what we find
              </span>
              <span className="block text-xs text-muted-foreground">
                Saved as a draft — you can edit everything.
              </span>
            </Label>
          </div>
        </div>

        {/* Organization type */}
        <div className="space-y-2">
          <Label>Organization type</Label>
          <Select value={data.type || ""} onValueChange={(v) => onUpdate({ ...data, type: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Select a type…" />
            </SelectTrigger>
            <SelectContent>
              {ORG_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <OrgTypeHelp value={data.type} />
        </div>
      </div>

      <div className="space-y-5">
        {/* Logo */}
        <div className="flex items-center gap-4">
          <div
            className="relative w-20 h-20 rounded-full overflow-hidden bg-muted flex items-center justify-center cursor-pointer group shrink-0"
            onClick={() => !uploadingLogo && logoInputRef.current?.click()}
          >
            {uploadingLogo ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : data.logo ? (
              <img src={data.logo} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              <Camera className="h-6 w-6 text-muted-foreground" />
            )}
            {!uploadingLogo && (
              <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                <Camera className="h-4 w-4 text-white mb-0.5" />
                <span className="text-[10px] text-white font-medium">Upload</span>
              </div>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">Organization logo</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">400 × 400px recommended</p>
          </div>
        </div>

        {/* Tagline */}
        <div className="space-y-2">
          <Label htmlFor="tagline">Tagline</Label>
          <Input
            id="tagline"
            value={data.tagline}
            maxLength={60}
            onChange={(e) => onUpdate({ ...data, tagline: e.target.value.slice(0, 60) })}
            placeholder="e.g. The brokerage built for agents who want more than a split"
          />
          <p className="text-[11px] text-muted-foreground/60 text-right">{data.tagline.length}/60</p>
        </div>
      </div>

      {/* Description */}
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor="description">About your organization</Label>
        <Textarea
          id="description"
          value={data.description}
          onChange={(e) => onUpdate({ ...data, description: e.target.value })}
          placeholder="A short paragraph on who you are and what makes you a great place for agents."
          className="min-h-[110px]"
        />
      </div>
    </div>
  );
};

export default OrgOnboardingStep2;
